import { faTrashAlt } from '@fortawesome/free-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { TFunction } from 'i18next'
import React, { FC, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import AppContext from '../../../gateway/Authentication/AppContext'
import Authorization from '../../../gateway/Authentication/Authorization'
import AuthService from '../../../gateway/Authentication/AuthService'
import DialogContext from '../../../gateway/Authentication/DialogContext'
import { ConfirmDialogProps } from '../../../gateway/components/ConfirmDialog'
import DataTable from '../../../gateway/components/DataTable'
import { EditDialogProps } from '../../../gateway/components/EditDialog'
import ProtectedButton from '../../../gateway/components/ProtectedButton'
import Loading from '../../../gateway/layout/Loading'
import MssqlUserService from '../service/MssqlUserService'

const MssqlUser: FC<{}> = () => {
  const { t } = useTranslation()
  const [users, setUsers] = useState([] as any[])

  const [loading, setLoading] = useState(true)
  const { userPermissions } = useContext(AppContext)
  const { confirmDialogPropsSetter, editDialogPropsSetter } = useContext(DialogContext)

  useEffect(() => {
    MssqlUserService.getUsers()
      .then((users) => setUsers(users))
      .finally(() => setLoading(false))
  }, [])

  const columns = useMemo(() => {
    return [
      {
        text: t('mssqlAdapter.name'),
        dataField: 'name',
        sort: true,
      },
      {
        text: t('mssqlAdapter.description'),
        dataField: 'description',
        sort: true,
      },
      {
        hidden: !AuthService.hasPermission(userPermissions, Authorization.services.mssql.editUser),
        dataField: 'df1',
        isDummyField: true,
        text: t('action'),
        headerAlign: 'center',
        classes: 'text-center',
        formatter: (cellData: any, row: any) => {
          return (
            <ProtectedButton
              permissions={Authorization.services.mssql.editUser}
              variant='link'
              className='text-danger text-link text-decoration-none'
              tooltip={t('mssqlAdapter.deleteUser')}
              onClick={() => deleteUser(row, setUsers, t, confirmDialogPropsSetter)}>
              <FontAwesomeIcon icon={faTrashAlt} />
            </ProtectedButton>
          )
        },
      },
    ]
  }, [t, confirmDialogPropsSetter, userPermissions])

  return (
    <Loading isLoading={loading}>
      <DataTable
        data={users}
        captionButtons={[
          {
            onClick: () => createUser(t, editDialogPropsSetter, setUsers),
            text: t('mssqlAdapter.createUser'),
            permissions: Authorization.services.mssql.editUser,
          },
        ]}
        columns={columns}
        caption={t('mssqlAdapter.users')}
      />
    </Loading>
  )
}

function createUser(t: TFunction, editDialogPropsSetter: any, setUsers: React.Dispatch<any>) {
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('mssqlAdapter.createUser'),
    onSubmit: MssqlUserService.createUser,
    onSubmitExtraArgs: [setUsers, t],
    labelGridSize: 5,
    data: [
      {
        id: 'name',
        type: 'text',
        label: t('mssqlAdapter.name'),
        placeholder: t('mssqlAdapter.name'),
        autoComplete: 'name',
        required: true,
        requiredMessage: t('invalidMessage', { label: t('mssqlAdapter.name') }),
      },
      {
        id: 'description',
        type: 'text',
        label: t('mssqlAdapter.description'),
        placeholder: t('mssqlAdapter.description'),
        autoComplete: 'description',
      },
    ],
  }

  editDialogPropsSetter(editDialogProps)
}

function deleteUser(row: any, setUsers: React.Dispatch<any>, t: TFunction, confirmDialogPropsSetter: any) {
  const disableIndexDialogProps: ConfirmDialogProps = {
    show: true,
    header: t('mssqlAdapter.deleteUser'),
    body: t('mssqlAdapter.deleteUserBody', { name: row.name }),
    submitText: t('yes'),
    onSubmit: MssqlUserService.deleteUser,
    onSubmitExtraArgs: [row.id, setUsers, t],
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(disableIndexDialogProps)
}

export default MssqlUser
